import { isEmpty } from 'lodash-es'

import { allContentByLabel, contentByLabel } from '@sunrise/shared'
import { Cms } from '@sunrise/types'
import { Content } from '@sunrise/types/dist/pageStructure/pageStructure'

import {
    MailAgentDisturber,
    ResultListDisturber,
} from '../../cmsElements/M/M_RealEstate_SearchList/M_RealEstate_SearchList.interface'

import { DecoratedEstate } from '../mapper/decoratedItemEstate.interface'
import { EsrKey, EsrType } from '../service/realEstateService.enum'
import { ESRType, Estate, SearchRequest } from '../service/realEstateService.interface'
import { CmsElement, CmsElementProperty } from '../shared/enum/cms.enum'
import { useResultListStore } from '../store/resultListStoreModule/resultListStoreModule'

export const processResultListDisturbers = (
    cmsContent: Cms.Content,
    totalResultListItems: number,
): ResultListDisturber | undefined => {
    const resultListDisturbers: Cms.Content<any>[] = allContentByLabel(cmsContent, CmsElement.DISTURBER_CONTAINER)

    if (isEmpty(resultListDisturbers)) {
        return
    }

    return buildResultListDisturbers(resultListDisturbers, totalResultListItems)
}

const buildResultListDisturbers = (
    resultListDisturber: Content<any>[],
    totalResultListItems: number,
): ResultListDisturber => {
    return {
        mailAgent: processMailAgentDisturber(resultListDisturber, totalResultListItems),
        adSlot: findAdSlotDisturbers(resultListDisturber),
    }
}

const processMailAgentDisturber = (
    resultListDisturber: Cms.Content<any>[],
    totalResultListItems: number,
): MailAgentDisturber | undefined => {
    for (const item of resultListDisturber) {
        if (isEmpty(item.children)) {
            continue
        }

        const mailAgentBanner: Cms.Content<any> | undefined = contentByLabel(item, CmsElement.MAIL_AGENT_BANNER)

        if (mailAgentBanner) {
            return {
                label: mailAgentBanner.label,
                index: determineDisturberPosition(item.properties, totalResultListItems),
            }
        }
    }
    return
}

const determineDisturberPosition = (disturberProperties: Cms.CmsProperties, totalItems: number): number => {
    const cmsPosition = Number(disturberProperties[CmsElementProperty.disturberPosition])
    const cmsIsLastPosition: Cms.CmsPropertiesValues = disturberProperties[CmsElementProperty.disturberIsLastPosition]

    return !cmsPosition || cmsIsLastPosition || cmsPosition >= totalItems
        ? totalItems - 1
        : Math.max(0, cmsPosition - 1)
}

const findAdSlotDisturbers = (resultListDisturbers: Cms.Content<any>[]): Cms.Content<any>[] => {
    return resultListDisturbers.filter((item: Cms.Content<any>) => {
        return (
            !isEmpty(item.children) &&
            !item.children?.some((child: Cms.Content<any>): boolean => child.name === CmsElement.MAIL_AGENT_BANNER)
        )
    })
}

export const updateSearchParams = (data: Partial<SearchRequest>): void => {
    useResultListStore().$patch({
        searchParams: data,
    })
}

export const getEsrKey = (esr: EsrType): EsrKey => (esr === EsrType.SELL ? EsrKey.BUY : EsrKey.RENT)

export const getSelectedEstateType = (estateTypes: ESRType[]): ESRType =>
    estateTypes.find((estateType) => estateType.selected)!

export const findMinMaxPriceInResults = (items: DecoratedEstate[] | Estate[], totalCount: number) => {
    let maxPrice = 0
    let minPrice = totalCount === 0 ? 0 : Number.MAX_VALUE

    for (const item of items) {
        const currentPrice = item.priceRaw || 0
        maxPrice = Math.max(maxPrice, currentPrice)
        minPrice = Math.min(minPrice, currentPrice)
    }

    return { maxPrice, minPrice }
}
